import React, { FC, useState } from "react";
import Button from "./button";
import Stack from "./stack";
import Heading from "./heading";
import Body from "./body";
import { keyframes } from "@emotion/react";
import Responsive from "./responsive";

interface ContractProps {
  contractId: string;
}

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const Text: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Body
      weight="BOLD"
      size="M"
      localStyles={{
        fontSize: 18,
        wordBreak: "break-word",
        margin: "0 auto",
      }}
    >
      {children}
    </Body>
  );
};

const truncateText = (text: string, maxLength: number = 20) => {
  if (text.length > maxLength) {
    const start = text.substring(0, Math.floor(maxLength / 2));
    const end = text.substring(text.length - Math.floor(maxLength / 2));
    return `${start}...${end}`;
  }
  return text;
};

const Contract: React.FC<ContractProps> = ({ contractId }) => {
  const [showTick, setShowTick] = useState(false);

  const handleCopy = (text: string) => async () => {
    try {
      await navigator.clipboard.writeText(text);
      setShowTick(true); // Show tick message
      setTimeout(() => setShowTick(false), 1000); // Hide after 1 second
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  return (
    <Stack
      direction="VERTICAL"
      space={1}
      localStyles={{
        alignItems: "center",
      }}
    >
      <Heading
        level="5"
        localStyles={{
          alignItems: "center",
          background: "rgba(0,0,0,0.3)",
          padding: "12px 12px 12px 16px",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        Meme coin Contract
      </Heading>
      <Stack
        direction="HORIZONTAL"
        space="24px"
        localStyles={{
          alignItems: "center",
          background: "rgba(0,0,0,0.2)",
          padding: "12px 12px 12px 16px",
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <Responsive below={419}>
          <Text>{truncateText(contractId, 10)}</Text>
        </Responsive>

        <Responsive middle={{ min: 420, max: 559 }}>
          <Text>{truncateText(contractId, 17)}</Text>
        </Responsive>

        <Responsive above={560}>
          <Text>{truncateText(contractId, 30)}</Text>
        </Responsive>

        <Stack
          direction="VERTICAL"
          localStyles={{
            justifyContent: "center",
            position: "relative",
            width: "auto",
          }}
        >
          <Button variant="PRIMARY" size="M" onClick={handleCopy(contractId)}>
            Copy
          </Button>
          {showTick && (
            <Body
              size="L"
              localStyles={{
                textAlign: "center",
                position: "absolute",
                width: "100%",
                right: -70,
                color: "pink",
                opacity: 1,
                animation: `${fadeOut} 1s ease-out`,
              }}
            >
              💨✨
            </Body>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Contract;
